import { supabase } from './supabase';

export interface GenerationCost {
  service_name: string;
  service_display_name: string;
  base_cost: number;
  description: string;
}

// Cache for generation costs to reduce database calls
const costCache = new Map<string, number>();

// Get cached cost or fetch from database
const getCachedCost = (serviceName: string): number | null => {
  const cached = costCache.get(serviceName);
  if (cached !== undefined) {
    return cached;
  }
  return null;
};

// Set cached cost
const setCachedCost = (serviceName: string, cost: number) => {
  costCache.set(serviceName, cost);
};

// Clear cache
const clearCostCache = () => {
  costCache.clear();
};

export const generationCostService = {
  // Get cost for a specific service
  async getServiceCost(serviceName: string): Promise<number> {
    try {
      // Check cache first
      const cachedCost = getCachedCost(serviceName);
      if (cachedCost !== null) {
        return cachedCost;
      }

      // Fetch from database
      const { data, error } = await supabase
        .rpc('get_generation_cost', { p_service_name: serviceName });

      if (error) {
        console.error('Error fetching generation cost:', error);
        // Return default costs as fallback
        const defaultCosts: Record<string, number> = {
          'moodboard': 10,
          'ai_touchup': 8,
          'ai_upstaging': 8,
          'ai_furniture_removal': 8,
          'image_upload': 1
        };
        return defaultCosts[serviceName] || 0;
      }

      const cost = data || 0;
      setCachedCost(serviceName, cost);
      return cost;
    } catch (error) {
      console.error('Error in getServiceCost:', error);
      return 0;
    }
  },

  // Get all generation costs
  async getAllGenerationCosts(): Promise<GenerationCost[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_all_generation_costs');

      if (error) {
        console.error('Error fetching all generation costs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllGenerationCosts:', error);
      return [];
    }
  },

  // Update generation cost (admin function)
  async updateGenerationCost(serviceName: string, newCost: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('update_generation_cost', {
          p_service_name: serviceName,
          p_new_cost: newCost
        });

      if (error) {
        console.error('Error updating generation cost:', error);
        return false;
      }

      // Clear cache to ensure fresh data
      clearCostCache();
      return data || false;
    } catch (error) {
      console.error('Error in updateGenerationCost:', error);
      return false;
    }
  },

  // Get costs for all main services
  async getMainServiceCosts(): Promise<Record<string, number>> {
    const services = ['moodboard', 'ai_touchup', 'ai_upstaging', 'ai_furniture_removal'];
    const costs: Record<string, number> = {};

    for (const service of services) {
      costs[service] = await this.getServiceCost(service);
    }

    return costs;
  },

  // Clear cache (useful for testing or when costs are updated)
  clearCache() {
    clearCostCache();
  },

  // Get formatted cost display string
  formatCostDisplay(serviceName: string, cost: number): string {
    const serviceDisplayNames: Record<string, string> = {
      'moodboard': 'Moodboard Generation',
      'ai_touchup': 'Edit',
      'ai_upstaging': 'Restyle',
      'ai_furniture_removal': 'AI Furniture Removal',
      'image_upload': 'Image Upload'
    };

    const displayName = serviceDisplayNames[serviceName] || serviceName;
    return `${displayName} (${cost} tokens)`;
  }
}; 